import { useCallback, useEffect, useRef, useState } from "react";

/** 업로드/삭제 시 '전달 중' 체감을 위한 최소 로딩 시간 */
export const INTENTIONAL_LOADING_MS = 1200;

interface UseMinDelayLoadingOptions {
    minDelayMs?: number;
    onComplete?: () => void;
}

/**
 * 최소 지연과 API 완료가 모두 충족될 때까지 로딩을 유지합니다.
 */
export function useMinDelayLoading({
    minDelayMs = INTENTIONAL_LOADING_MS,
    onComplete,
}: UseMinDelayLoadingOptions = {}) {
    const [isLoading, setIsLoading] = useState(false);
    const minDelayDoneRef = useRef(false);
    const apiDoneRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCompleteRef = useRef(onComplete);
    const afterCompleteRef = useRef<(() => void) | null>(null);
    onCompleteRef.current = onComplete;

    const tryComplete = useCallback(() => {
        if (!minDelayDoneRef.current || !apiDoneRef.current) return;
        onCompleteRef.current?.();
        afterCompleteRef.current?.();
        afterCompleteRef.current = null;
        setIsLoading(false);
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
    }, []);

    const start = useCallback(() => {
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
        afterCompleteRef.current = null;
        setIsLoading(true);

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            minDelayDoneRef.current = true;
            tryComplete();
        }, minDelayMs);
    }, [minDelayMs, tryComplete]);

    const done = useCallback((afterComplete?: () => void) => {
        afterCompleteRef.current = afterComplete ?? null;
        apiDoneRef.current = true;
        tryComplete();
    }, [tryComplete]);

    const error = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        afterCompleteRef.current = null;
        setIsLoading(false);
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return { isLoading, start, done, error };
}
